import React from "react";
import { connect } from "react-redux";
import { HashRouter, Switch, Redirect } from "react-router-dom";
import { Container } from "react-bootstrap";
import history from "./history";
import LayoutPointer from "./components/RouteLayouts/LayoutPointer";
import LoginRouteLayout from "./components/RouteLayouts/LoginRouteLayout";
import StudentLoginPage from "./pages/StudentLoginPage";
import SsoLoginPage from "./pages/SsoLoginPage";
import { API_ROOT } from "./apis/ApiConfig";
import IdleAlert from "./components/Alerts/IdleAlert";
import loadable from '@loadable/component';
import ErrorBoundary from "./utilities/ErrorBoundary";
import { stagingHostname } from "./utilities/ApiConstants";
import {CorrectionsDisplayPage} from "./pages/CorrectionsDisplayPage"

const PostcardsReportPage = loadable(() => import('./pages/PostcardsReportPage'));
const ResetPasswordPage = loadable(() => import('./pages/ResetPasswordPage'));
const HolidaysPage = loadable(() => import('./pages/HolidaysPage'));
const NotificationsPage = loadable(() => import('./pages/NotificationsPage'));
const StudentAccessLogPage = loadable(() => import('./pages/StudentAccessLogPage'));
const AdminAccessLogPage = loadable(() => import('./pages/AdminAccessLogPage'));
const VerificationReportPage = loadable(() => import('./pages/VerificationReportPage'));
const MetricsPage = loadable(() => import('./pages/MetricsPage'));
const CRInterviewCreationPage = loadable(() => import('./pages/CRInterviewCreationPage'));
const AddSchoolPage = loadable(() => import('./pages/AddSchoolPage'));
const SchoolAnnualRolloverPage = loadable(() => import('./pages/SchoolAnnualRolloverPage'));
const SchoolAuditPage = loadable(() => import('./pages/SchoolAuditPage'));
const StudentRepoPage = loadable(() => import('./pages/StudentRepoPage'));
const StudentRepoUploadReportPage = loadable(() => import('./pages/StudentRepoUploadReportPage'));
const StudentRepoErrorReportPage = loadable(() => import('./pages/StudentRepoErrorReportPage'));
const StudentRepoErrorDetailsPage = loadable(() => import('./pages/StudentRepoErrorDetailsPage'));
const StudentRepoUploadDetailsPage = loadable(() => import('./pages/StudentRepoUploadDetailsPage'));
const MissingStudentReportPage = loadable(() => import('./pages/MissingStudentReportPage'));
const NonMatchingSSNReportPage = loadable(() => import('./pages/NonMatchingSSNReportPage'));
const DependencyChangeReportPage = loadable(() => import('./pages/DependencyChangeReportPage'));
const AccountOwnershipReportPage = loadable(() => import('./pages/AccountOwnershipReportPage'));
const WatchlistReportPage = loadable(() => import('./pages/WatchlistReportPage'));
const ChangeReportPage = loadable(() => import('./pages/ChangeReportPage'));
const ApprovedWithCRReportPage = loadable(() => import('./pages/ApprovedWithCRReportPage'));
const AutoVerifyReportPage = loadable(() => import('./pages/AutoVerifyReportPage'));
const UserLandingPage = loadable(() => import('./pages/UserLandingPage'));
const MultiFactorAuthenticationPage = loadable(() => import('./pages/MultiFactorAuthenticationPage'));
const InterviewHistoryPage = loadable(() => import('./pages/InterviewHistoryPage'));
const ApprovedWithCorrectionsReportPage = loadable(() => import('./pages/ApprovedWithCorrectionsReportPage'));
const NewAccountPasswordResetPage = loadable(() => import('./pages/NewAccountPasswordResetPage'));
const AccountQCReportPage = loadable(() => import('./pages/AccountQCReportPage'));
const WelcomePage = loadable(() => import('./pages/WelcomePage'));
const UserWelcomePage = loadable(() => import('./pages/UserWelcomePage'));
const PageNotImplemented = loadable(() => import('./pages/PageNotImplemented'));
const PageNotFound = loadable(() => import('./pages/PageNotFound'));
const IsirUploadPage = loadable(() => import('./pages/IsirUploadPage'));
const StudentViewPage = loadable(() => import('./pages/StudentViewPage'));
const UserSearchPage = loadable(() => import('./pages/UserSearchPage'));
const GeneralReportPage = loadable(() => import('./pages/GeneralReportPage'));
const SuperBinderReportPage = loadable(() => import('./pages/SuperBinderReportPage'));
const CommentCodeReportPage = loadable(() => import('./pages/CommentCodeReportPage'));
const CorrectionAutoVerifyReportPage = loadable(() => import('./pages/CorrectionAutoVerifyReportPage'));
const CrossYearDataReportPage = loadable(() => import('./pages/CrossYearDataReportPage'));
const AllStudentsReportPage = loadable(() => import('./pages/AllStudentsReportPage'));
const EducationalPurposeReportPage = loadable(() => import('./pages/EducationalPurposeReportPage'));
const SchoolAidYearSettingsPage = loadable(() => import('./pages/SchoolAidYearSettingsPage'));
const GlobalSchoolSettings = loadable(() => import('./pages/GlobalSchoolSettings'));
const isirViewSettings = loadable(() => import('./pages/isirViewSettings'));
const AutoVerifyDesignPage = loadable(() => import('./pages/AutoVerifyDesignPage'));
const AdminAccountCreationPage = loadable(() => import('./pages/AdminAccountCreationPage'));
const InterviewPage = loadable(() => import('./pages/InterviewPage'));
const InterviewCreationPage = loadable(() => import('./pages/InterviewCreationPage'));
const StudentLookupPage = loadable(() => import('./pages/StudentLookupPage'));
const CommunicationLogPage = loadable(() => import('./pages/CommunicationLogPage'));
const StudentAccountCreationPage = loadable(() => import('./pages/StudentAccountCreationPage'));
const MessageTemplates = loadable(() => import('./pages/MessageTemplates'));
const PovertyLevelsPage = loadable(() => import('./pages/PovertyLevelsPage'));
const UserSettingsPage = loadable(() => import('./pages/UserSettingsPage'));
const PermissionsPage = loadable(() => import('./pages/PermissionsPage'));
const SAIGMailboxAdminPage = loadable(() => import('./pages/SAIGMailboxAdminPage'));
const IsirLogPage = loadable(() => import('./pages/IsirLogPage'));
const IsirListPage = loadable(() => import('./pages/IsirListPage'));
const CorrectionsListPage = loadable(() => import('./pages/CorrectionsListPage'));
const TransmittedCorrectionsPage = loadable(() => import('./pages/TransmittedCorrectionsPage'));
const SAIGMailboxPage = loadable(() => import('./pages/SAIGMailboxPage'));
const ReviewReportPage = loadable(() => import('./pages/ReviewReportPage'));
const ApprovedReportPage = loadable(() => import('./pages/ApprovedReportPage'));
const ForgotPasswordPage = loadable(() => import('./pages/ForgotPasswordPage'));
const JobsPage = loadable(() => import('./pages/JobsPage'));
const SettingsPage = loadable(() => import('./pages/SettingsPage'));
const InstitutionalSelectedVerificationReportPage = loadable(() => import('./pages/InstitutionalSelectedVerificationReportPage'));

class App extends React.Component {

  renderRoutes() {
    return (
      <ErrorBoundary>
        {this.props.auth.isSignedIn && <IdleAlert></IdleAlert>}

        <Switch>
          <Redirect from="/userWelcome" exact to="/login" />
          <Redirect from="/" exact to="/login" />
          <LoginRouteLayout path="/login" exact component={StudentLoginPage} />
          <LoginRouteLayout path="/mfa/:uuid/:accountType" exact component={MultiFactorAuthenticationPage} />
          <LoginRouteLayout
            path="/userLanding/:uuid/:accountType"
            exact
            component={UserLandingPage}
          />
          {!API_ROOT.includes(".verificationgateway") &&
            API_ROOT !== "prod-lb-external-938615202.us-east-1.elb.amazonaws.com" && (
              <LoginRouteLayout
                path="/ssoLogin"
                exact
                component={SsoLoginPage}
              />
            )}
          <LoginRouteLayout
            path="/login/:domain"
            exact
            component={StudentLoginPage}
          />
          <LoginRouteLayout
            path="/forgotPassword"
            exact
            component={ForgotPasswordPage}
          />
          <LoginRouteLayout
            path="/forgotPassword/:domain"
            exact
            component={ForgotPasswordPage}
          />
          <LoginRouteLayout
            path="/newAccountPasswordReset"
            exact
            component={NewAccountPasswordResetPage}
          />
          <LoginRouteLayout
            path="/resetPassword/:uuid/:token/:accountType"
            exact
            component={ResetPasswordPage}
          />
          <LoginRouteLayout
            path="/resetPassword/:uuid/:token/:accountType/:domain"
            exact
            component={ResetPasswordPage}
          />
          <LoginRouteLayout
            path="/studentLookup"
            exact
            component={StudentLookupPage}
          />
          <LoginRouteLayout
            path="/studentLookup/:domain"
            exact
            component={StudentLookupPage}
          />
          <LoginRouteLayout
            path="/studentAccountCreation/:uuid/:accountType/:aidYear"
            exact
            component={StudentAccountCreationPage}
          />
          <LoginRouteLayout
            path="/studentAccountCreation/:uuid/:accountType/:aidYear/:domain"
            exact
            component={StudentAccountCreationPage}
          />
          <LayoutPointer
            exact
            component={UserWelcomePage}
            path="/student/:uuid"
          ></LayoutPointer>
          <LayoutPointer
            exact
            component={UserWelcomePage}
            path="/parent/:uuid"
          ></LayoutPointer>
          <LayoutPointer path="/welcome" exact component={WelcomePage} />
          <LayoutPointer path="/userSearch" exact component={UserSearchPage} />
          <LayoutPointer
            path="/povertyLevels"
            exact
            component={PovertyLevelsPage}
          />
          <LayoutPointer
            path="/userSettings"
            exact
            component={UserSettingsPage}
          />
          <LayoutPointer
            path="/jobs"
            exact
            component={JobsPage}
          />
          <LayoutPointer path="/addSchool" exact component={AddSchoolPage} />
          <LayoutPointer
            path="/communicationLog"
            exact
            component={CommunicationLogPage}
          />
          <LayoutPointer path="/Metrics" exact component={MetricsPage} />
          <LayoutPointer
            path="/SchoolAidYearSettings"
            exact
            component={SchoolAidYearSettingsPage}
          />
          <LayoutPointer
            path="/GeneralReport"
            exact
            component={GeneralReportPage}
          />
          {/* <LayoutPointer
            path="/PendingTaskListItemReport"
            exact
            component={PendingTaskListItemReportPage}
          /> */}
          <LayoutPointer
            path="/AutoVerifyReport"
            exact
            component={AutoVerifyReportPage}
          />
          <LayoutPointer
            path="/CorrectionAutoVerifyReport"
            exact
            component={CorrectionAutoVerifyReportPage}
          />
          <LayoutPointer
            path="/MissingStudentReport"
            exact
            component={MissingStudentReportPage}
          />
          <LayoutPointer
            path="/NonMatchingSSNReport"
            exact
            component={NonMatchingSSNReportPage}
          />
          <LayoutPointer
            path="/DependencyChangeReport"
            exact
            component={DependencyChangeReportPage}
          />
          <LayoutPointer
            path="/ChangeReport"
            exact
            component={ChangeReportPage}
          />
          <LayoutPointer
            path="/AccountOwnershipReport"
            exact
            component={AccountOwnershipReportPage}
          />
          <LayoutPointer
            path="/AccountQCReport"
            exact
            component={AccountQCReportPage}
          />
          <LayoutPointer
            path="/WatchlistReport"
            exact
            component={WatchlistReportPage}
          />
          <LayoutPointer
            path="/AllStudentsReport"
            exact
            component={AllStudentsReportPage}
          />
          <LayoutPointer
            path="/CrossYearDataReport"
            exact
            component={CrossYearDataReportPage}
          />
          <LayoutPointer
            path="/InstitutionalSelectedVerificationReportPage"
            exact
            component={InstitutionalSelectedVerificationReportPage}
          />
          <LayoutPointer
            path="/PostcardsReport"
            exact
            component={PostcardsReportPage}
          />
          <LayoutPointer
            path="/VerificationReport"
            exact
            component={VerificationReportPage}
          />
          <LayoutPointer
            path="/SuperBinderReport"
            exact
            component={SuperBinderReportPage}
          />
          <LayoutPointer
            path="/CommentCodeReport"
            exact
            component={CommentCodeReportPage}
          />
          <LayoutPointer
            path="/GlobalSchoolSettings"
            exact
            component={GlobalSchoolSettings}
          />
          <LayoutPointer
            path="/isirViewSettings"
            exact
            component={isirViewSettings}
          />
          {API_ROOT !== "https://testcollege.verificationgateway.org/gateway" &&
            API_ROOT !==
              "https://coffeyvillecommunitycollege.verificationgateway.org/gateway" &&
            API_ROOT !==
              "https://tcfinancialaid.verificationgateway.org/gateway" &&
            API_ROOT !== `https://${stagingHostname}/gateway` && (
              <LayoutPointer
                path="/autoVerifyDesign"
                exact
                component={AutoVerifyDesignPage}
              />
            )}
          <LayoutPointer path="/upload" exact component={IsirUploadPage} />
          <LayoutPointer path="/isirLog" exact component={IsirLogPage} />
          <LayoutPointer
            path="/studentRepo"
            exact
            component={StudentRepoPage}
          />
          <LayoutPointer
            path="/studentRepoUploadReport"
            exact
            component={StudentRepoUploadReportPage}
          />
          <LayoutPointer
            path="/studentRepoErrorReport"
            exact
            component={StudentRepoErrorReportPage}
          />
          <LayoutPointer
            path="/studentRepoErrorDetails/:errorDetails"
            exact
            component={StudentRepoErrorDetailsPage}
          />
          <LayoutPointer
            path="/studentRepoUploadDetails/:uploadDetails"
            exact
            component={StudentRepoUploadDetailsPage}
          />

          <LayoutPointer
            path="/IsirListPage/:isirid"
            exact
            component={IsirListPage}
          />
          <LayoutPointer
            path="/corrections"
            exact
            component={CorrectionsListPage}
          />
          <LayoutPointer
            path="/transmittedCorrections"
            exact
            component={TransmittedCorrectionsPage}
          />
          <LayoutPointer
            path="/saigMailbox"
            exact
            component={SAIGMailboxPage}
          />
          <LayoutPointer
            path="/reviewWorklist"
            exact
            component={ReviewReportPage}
          />
          <LayoutPointer
            path="/approvedWorklist"
            exact
            component={ApprovedReportPage}
          />
          <LayoutPointer
            path="/approvedWithCorrectionsWorklist"
            exact
            component={ApprovedWithCorrectionsReportPage}
          />
          <LayoutPointer
            path="/approvedWithCRWorklist"
            exact
            component={ApprovedWithCRReportPage}
          />
          <LayoutPointer
            path="/EducationalPurposeReportPage"
            exact
            component={EducationalPurposeReportPage}
          />
          <LoginRouteLayout path="/404" exact component={PageNotFound} />
          <LayoutPointer
            path="/MessageTemplates"
            exact
            component={MessageTemplates}
          />
          <LayoutPointer
            path="/SchoolProfile"
            exact
            component={PageNotImplemented}
          />
          <LayoutPointer
            path="/SchoolAnnualRollover"
            exact
            component={SchoolAnnualRolloverPage}
          />
          <LayoutPointer
            path="/IsirViewSettings"
            exact
            component={PageNotImplemented}
          />
          <LayoutPointer
            path="/Permissions"
            exact
            component={PermissionsPage}
          />
          <LayoutPointer
            path="/SAIGMailboxAdministration"
            exact
            component={SAIGMailboxAdminPage}
          />
          <LayoutPointer path="/Holidays" exact component={HolidaysPage} />
          <LayoutPointer
            path="/Notifications"
            exact
            component={NotificationsPage}
          />
          <LayoutPointer
            path="/AdministrativeActivity"
            exact
            component={AdminAccessLogPage}
          />
          <LayoutPointer
            path="/StudentActivity"
            exact
            component={StudentAccessLogPage}
          />
          <LayoutPointer
            path="/SchoolAudit"
            exact
            component={SchoolAuditPage}
          />
          <LayoutPointer
            path="/notImplemented"
            exact
            component={PageNotImplemented}
          />
          <LayoutPointer
            userAllowed
            path="/StudentView/:uuid/:component/:studentInterviewLinkUUID"
            component={InterviewPage}
          />
          <LayoutPointer
            userAllowed
            path="/StudentView/:uuid/:component/"
            component={StudentViewPage}
          />
          <LayoutPointer
            userAllowed
            path="/Settings/:uuid/:component/"
            component={SettingsPage}
          />
          <LayoutPointer
            path="/adminAccountCreation/:uuid/:accountType"
            component={AdminAccountCreationPage}
          />
          <LayoutPointer path="/Interview/:uuid" component={InterviewPage} />
          <LayoutPointer
            path="/InterviewHistory/:uuid/:studentInterviewLinkUUID"
            component={InterviewHistoryPage}
            hideHeader
            hideFooter
          />
          <LayoutPointer
            path="/PageNotImplemented"
            component={PageNotImplemented}
          />
          <LayoutPointer
            path="/CorrectionsDisplay"
            component={CorrectionsDisplayPage}
          />
          {API_ROOT !==
            "https://coffeyvillecommunitycollege.verificationgateway.org/gateway" &&
            API_ROOT !==
              "https://tcfinancialaid.verificationgateway.org/gateway" && (
              <>
                <LayoutPointer
                  path="/InterviewCreation"
                  component={InterviewCreationPage}
                />
                <LayoutPointer
                  path="/CRInterviewCreation"
                  component={CRInterviewCreationPage}
                />
              </>
            )}
          <Redirect
            from="/StudentView/:uuid"
            exact
            to="/StudentView/:uuid/quickView"
          />
          <LayoutPointer path="/" component={PageNotFound} />
        </Switch>
      </ErrorBoundary>
    );
  }

  render() {
    return (
      <Container fluid id="pageContainer">
        <HashRouter history={history}>{this.renderRoutes()}</HashRouter>
      </Container>
    );
  }
}
const mapStateToProps = (state) => {
  return { auth: state.auth };
};
export default connect(mapStateToProps, {})(App);
